import React, { useState, useEffect } from 'react';
import {
  Box, Heading, VStack, Text, Button, HStack, Icon, Container, SimpleGrid,
  Image, Tabs, TabList, TabPanels,Tag,  Tab, TabPanel, useToast, Spinner, Center,
  Progress, Divider, Tooltip, Modal, ModalOverlay, ModalContent, ModalHeader,
  ModalCloseButton, ModalBody, ModalFooter, useDisclosure, IconButton, useBreakpointValue
} from '@chakra-ui/react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaTrophy, FaUserPlus, FaCheckCircle, FaArrowLeft, FaArrowRight } from 'react-icons/fa';
import { useAuthCliente } from '../context/AuthClienteContext';
import { db } from '../firebase/firebase-config';
import { collection, query, onSnapshot, doc, updateDoc, arrayUnion, orderBy } from 'firebase/firestore';
import { getRoundLabelsForPoints, getPhaseName } from '../utils/tournamentLogic';
import TournamentBracketModal from '../components/TournamentBracketModal';

// --- Modal de Confirmación de Inscripción ---
function RegistrationConfirmModal({ isOpen, onClose, onConfirm, isLoading }) {
  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Confirmar Pre-inscripción</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={3}>
            <Text>Nos da mucho gusto que quieras participar, por favor considera que para que tu inscripción quede registrada y tu lugar no se pierda, debes hacer tu pago de inscripción en las oficinas del club.</Text>
            <Text fontWeight="bold">De lo contrario, a pesar de que estés registrado por este medio, si otros jugadores dan su pago de inscripción no podremos conservar tu lugar.</Text>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>Cancelar</Button>
          <Button colorScheme="green" onClick={onConfirm} isLoading={isLoading}>
            Confirmar Inscripción
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

// --- Tarjeta para Torneos ACTIVOS ---
function ActiveTournamentCard({ tournament }) {
  const { currentUser } = useAuthCliente();
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isRegistering, setIsRegistering] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  const deadline = tournament.registrationDeadline?.toDate();
  let daysLeft = null;
  if (deadline) {
    const today = new Date();
    const diffTime = deadline.getTime() - today.getTime();
    daysLeft = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  }
  const isUserRegistered = tournament.participants?.includes(currentUser.uid);
  const isFull = tournament.participants?.length >= tournament.maxPlayers;
  const spotsLeft = tournament.maxPlayers - (tournament.participants?.length || 0);
  const isClosed = tournament.status !== 'open';
  const deadlinePassed = daysLeft !== null && daysLeft < 0;
  const pointLabels = getRoundLabelsForPoints(tournament.maxPlayers);

  const handleRegister = async () => {
    if (!currentUser) return;
    setIsRegistering(true);
    try {
      const tournamentRef = doc(db, 'companies', currentUser.companyId, 'tournaments', tournament.id);
      await updateDoc(tournamentRef, {
        participants: arrayUnion(currentUser.uid)
      });
      toast({ 
        title: "¡Pre-inscripción exitosa!", 
        description: "Recuerda completar tu pago en el club.",
        status: 'success', 
        isClosable: true, 
        duration: 6000 
      });
      onClose();
    } catch (error) {
      toast({ 
        title: "Error en la inscripción", 
        description: error.message, 
        status: 'error', 
        isClosable: true 
      });
    } finally {
      setIsRegistering(false);
    }
  };
  
  return (
    <>
      <RegistrationConfirmModal 
        isOpen={isOpen} 
        onClose={onClose}
        onConfirm={handleRegister}
        isLoading={isRegistering}
      />
      <Box
        h="520px"
        position="relative"
        bg="white"
        borderRadius="xl"
        boxShadow="lg"
        transition="all 0.2s"
        _hover={{ transform: 'translateY(-5px)', boxShadow: '2xl' }}
        overflow="hidden"
      >
        <AnimatePresence>
          {!showDetails ? (
            <motion.div
              key="summary"
              style={{ width: '100%', height: '100%' }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <VStack w="full" h="full" spacing={0}>
                <Image src={tournament.imageUrl} fallbackSrc="https://via.placeholder.com/400x150.png?text=Torneo" alt={`Imagen de ${tournament.name}`} h="150px" w="full" objectFit="cover" />
                <VStack p={4} align="stretch" w="full" spacing={3} flex={1}>
                  <Heading size="md">{tournament.name}</Heading>
                  <Text fontSize="sm" color="gray.500">{tournament.dates}</Text>
                  <Box>
                    <Text fontSize="sm" fontWeight="bold">Inscritos: {tournament.participants?.length || 0} / {tournament.maxPlayers}</Text>
                    <Progress value={(tournament.participants?.length || 0)} max={tournament.maxPlayers} size="sm" colorScheme="green" mt={1} />
                    <Text fontSize="xs" color="gray.500">{spotsLeft} lugares disponibles</Text>
                  </Box>
                  <Text fontWeight="bold" fontSize="lg" color="brand.600">
                    Costo: ${tournament.cost || 0} MXN
                  </Text>
                  <VStack bg="gray.50" p={2} borderRadius="md" spacing={1}>
                    <Text fontSize="xs" fontWeight="bold" color="gray.600">CIERRE DE INSCRIPCIONES</Text>
                    <Text fontSize="sm">{deadline ? deadline.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' }) : 'No definida'}</Text>
                    {daysLeft !== null && daysLeft >= 0 && (
                      <Tag size="sm" colorScheme={daysLeft < 3 ? 'red' : 'green'}>
                        {daysLeft > 1 ? `Quedan ${daysLeft} días` : (daysLeft === 1 ? 'Queda 1 día' : '¡Cierra hoy!')}
                      </Tag>
                    )}
                  </VStack>
                  <Box mt="auto" pt={3} textAlign="center">
                    {isUserRegistered ? (
                      <Button colorScheme="green" variant="outline" w="full" size="sm" leftIcon={<FaCheckCircle />}>Ya estás inscrito</Button>
                    ) : (
                      <Tooltip 
                        label={deadlinePassed ? "La fecha de inscripción ha terminado" : "Este evento se ha cerrado"}
                        isDisabled={!isClosed && !deadlinePassed}
                      >
                        <Box w="full">
                          <Button 
                            w="full" colorScheme="purple" size="sm" leftIcon={<FaUserPlus />}
                            onClick={onOpen}
                            isDisabled={isFull || isClosed || deadlinePassed}
                          >
                            {isFull ? 'Torneo Lleno' : (isClosed || deadlinePassed ? 'Inscripciones Cerradas' : 'Inscribirme')}
                          </Button>
                        </Box>
                      </Tooltip>
                    )}
                    <Button variant="link" size="sm" mt={2} onClick={() => setShowDetails(true)}>Ver Detalles</Button>
                  </Box>
                </VStack>
              </VStack>
            </motion.div>
          ) : (
            <motion.div
              key="details"
              style={{ width: '100%', height: '100%' }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <VStack
                w="full" h="full" bg="gray.800" color="white"
                p={5} spacing={3} align="stretch"
              >
                <Heading size="md" textAlign="center">{tournament.name}</Heading>
                <Divider borderColor="gray.600" />
                <VStack align="stretch" spacing={2} w="full" flex={1} overflowY="auto" pr={2}>
                  <HStack justify="space-between"><Text fontWeight="bold">Categoría:</Text> <Text>{tournament.category}</Text></HStack>
                  <HStack justify="space-between"><Text fontWeight="bold">Formato:</Text> <Text>{tournament.format}</Text></HStack>
                  <HStack justify="space-between"><Text fontWeight="bold">Género:</Text> <Text>{tournament.gender}</Text></HStack>
                  <HStack justify="space-between"><Text fontWeight="bold">Premio:</Text> <Text>{tournament.prize || 'N/A'}</Text></HStack>
                  <Divider borderColor="gray.600" my={2} />
                  <Text fontWeight="bold" textAlign="center">Puntos por Ronda</Text>
                  {Object.entries(pointLabels || {}).map(([key, label]) => (
                      <HStack justify="space-between" key={key}>
                          <Text color="gray.300">{label}:</Text>
                          <Text fontWeight="bold">{tournament.pointConfig?.[key] || 0} pts</Text>
                      </HStack>
                  ))}
                </VStack>
                <Button variant="link" colorScheme="white" size="sm" onClick={() => setShowDetails(false)}>Volver a Inscripción</Button>
              </VStack>
            </motion.div>
          )}
        </AnimatePresence>
      </Box>
    </>
  );
}

// --- Tarjeta para Torneos PASADOS y EN CURSO (Con la modificación) ---
function InfoTournamentCard({ tournament, onClick }) {
  const [showDetails, setShowDetails] = useState(false);
  const date = tournament.status === 'completed' 
    ? tournament.createdAt?.toDate().toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' })
    : tournament.dates;
  
  const pointLabels = getRoundLabelsForPoints(tournament.players?.length || 0);

  return (
    <Box
      h="320px"
      position="relative"
      bg="white"
      borderRadius="xl"
      boxShadow="lg"
      p={5}
      transition="all 0.2s"
      _hover={{ transform: 'translateY(-5px)', boxShadow: '2xl' }}
      cursor="pointer"
    >
      <AnimatePresence>
        {!showDetails ? (
          <motion.div
            key="summary"
            style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={() => setShowDetails(true)}
          >
            <VStack w="full" h="full" p={5} spacing={4} textAlign="center" justifyContent="center">
              <Heading size="md">{tournament.name}</Heading>
              <Text fontSize="sm" color="gray.500">{date}</Text>
              <Divider />
              {tournament.status === 'completed' ? (
                // ✅ VISTA PARA TORNEOS COMPLETADOS (MODIFICADA)
                <VStack spacing={4}>
                  <Icon as={FaTrophy} w={12} h={12} color="yellow.400" />
                  <Box>
                    <Text fontSize="sm" color="gray.600">Campeón</Text>
                    <Heading size="lg" color="brand.600">{tournament.winnerInfo?.name || 'Indefinido'}</Heading>
                  </Box>
                  {/* BOTÓN AÑADIDO */}
                  <Button 
                  _hover={{ bg: 'green.600', color: 'white', cursor: 'pointer' }}
                    size="sm" 
                    variant="outline"
                    colorScheme="gray"
                    onClick={(e) => { e.stopPropagation(); onClick(); }}
                  >
                    Ver Resultados
                  </Button>
                </VStack>
              ) : (
                // VISTA PARA TORNEOS EN CURSO (SIN CAMBIOS)
                <VStack spacing={4}>
                    <Tag colorScheme="blue">En Progreso</Tag>
                    <Text>¡Sigue los resultados en vivo!</Text>
                    <Button  _hover={{ bg: 'purple.600', color: 'white', cursor: 'pointer' }} size="sm" onClick={(e) => { e.stopPropagation(); onClick(); }}>Ver Cuadro</Button>
                </VStack>
              )}
            </VStack>
          </motion.div>
        ) : (
          // ... (código de la parte trasera de la tarjeta, sin cambios)
          <motion.div
            key="details"
            style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={() => setShowDetails(false)}
          >
            <VStack
              w="full" h="full" bg="gray.800" color="white"
              borderRadius="xl" p={5} spacing={3} align="stretch" overflowY="auto"
            >
              <Heading size="md" textAlign="center">{tournament.name}</Heading>
              <Divider borderColor="gray.600" />
              <VStack align="stretch" spacing={2} w="full">
                <HStack justify="space-between"><Text fontWeight="bold">Categoría:</Text> <Text>{tournament.category || tournament.categoría}</Text></HStack>
                <HStack justify="space-between"><Text fontWeight="bold">Formato:</Text> <Text>{tournament.format || tournament.formato}</Text></HStack>
                <HStack justify="space-between"><Text fontWeight="bold">Género:</Text> <Text>{tournament.gender || tournament.genero}</Text></HStack>
              </VStack>
              <Divider borderColor="gray.600" />
              <Text fontWeight="bold" textAlign="center">Puntos por Ronda</Text>
              <VStack align="stretch" spacing={1} fontSize="sm" w="full">
                {Object.entries(pointLabels || {}).map(([key, label]) => (
                    <HStack justify="space-between" key={key}>
                        <Text color="gray.300">{label}:</Text>
                        <Text fontWeight="bold">{tournament.pointConfig?.[key] || 0} pts</Text>
                    </HStack>
                ))}
              </VStack>
            </VStack>
          </motion.div>
        )}
      </AnimatePresence>
    </Box>
  );
}

export default function TorneosPage() {
  const { currentUser } = useAuthCliente();
  const [tournaments, setTournaments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const { isOpen: isBracketOpen, onOpen: onBracketOpen, onClose: onBracketClose } = useDisclosure();
  const [selectedTournament, setSelectedTournament] = useState(null);
  
  // Estados de paginación
  const [openPage, setOpenPage] = useState(0);
  const [inProgressPage, setInProgressPage] = useState(0);
  const [pastPage, setPastPage] = useState(0);
  const itemsPerPage = useBreakpointValue({ base: 1, md: 2, lg: 3 }) || 3;

  useEffect(() => {
    if (!currentUser?.companyId) {
        setIsLoading(false);
        return;
    };
    setIsLoading(true);
    const ref = collection(db, 'companies', currentUser.companyId, 'tournaments');
    const q = query(ref, orderBy('createdAt', 'desc'));
    
    const unsub = onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const officialTournaments = data.filter(t => t.type !== 'reta');
      setTournaments(officialTournaments);
      setIsLoading(false);
    }, (error) => {
      console.error("Error fetching tournaments:", error);
      setIsLoading(false);
    });
    return () => unsub();
  }, [currentUser?.companyId]);


   const openTournaments = tournaments.filter(t => t.status === 'open');
  const inProgressTournaments = tournaments.filter(t => t.status === 'in-progress' || t.status === 'closed');
  const pastTournaments = tournaments.filter(t => t.status === 'completed');
  
  // Lógica de paginación para cada sección
  const totalOpenPages = Math.ceil(openTournaments.length / itemsPerPage);
  const paginatedOpen = openTournaments.slice(openPage * itemsPerPage, (openPage + 1) * itemsPerPage);

  const totalInProgressPages = Math.ceil(inProgressTournaments.length / itemsPerPage);
  const paginatedInProgress = inProgressTournaments.slice(inProgressPage * itemsPerPage, (inProgressPage + 1) * itemsPerPage);

  const totalPastPages = Math.ceil(pastTournaments.length / itemsPerPage);
  const paginatedPast = pastTournaments.slice(pastPage * itemsPerPage, (pastPage + 1) * itemsPerPage);

  const handleBracketClick = (tournament) => {
    setSelectedTournament(tournament);
    onBracketOpen();
  };

  if (isLoading) {
    return <Center h="50vh"><Spinner size="xl" /></Center>;
  }

  return (
    <Container maxW="container.xl" py={8}>
      <TournamentBracketModal isOpen={isBracketOpen} onClose={onBracketClose} tournament={selectedTournament} />
      <VStack spacing={12} align="stretch">
        <HStack>
          <Icon as={FaTrophy} w={10} h={10} color="yellow.400" />
          <Heading size="2xl">Torneos</Heading>
        </HStack>
        
        {/* ✅ CONTENEDOR SUPERIOR CON PESTAÑAS ACTUALIZADAS */}
        <Box bg="white" p={6} borderRadius="xl" boxShadow="lg">
          <Tabs isFitted variant="soft-rounded" colorScheme="green">
            <TabList mb="1em">
              <Tab>Inscripciones Abiertas</Tab>
              <Tab>Torneos en Curso</Tab>
            </TabList>
            <TabPanels>
              {/* Pestaña de Inscripciones Abiertas */}
              <TabPanel p={4}>
                {openTournaments.length > 0 ? (
                  <VStack align="stretch" spacing={6}>
                    <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
                      {paginatedOpen.map(tournament => (
                        <ActiveTournamentCard key={tournament.id} tournament={tournament} />
                      ))}
                    </SimpleGrid>
                    {totalOpenPages > 1 && (
                      <HStack justify="center" mt={4}>
                        <IconButton icon={<FaArrowLeft />} aria-label="Anterior" onClick={() => setOpenPage(p => p - 1)} isDisabled={openPage === 0} />
                        <Text fontSize="sm" color="gray.600">{openPage + 1} / {totalOpenPages}</Text>
                        <IconButton icon={<FaArrowRight />} aria-label="Siguiente" onClick={() => setOpenPage(p => p + 1)} isDisabled={openPage >= totalOpenPages - 1} />
                      </HStack>
                    )}
                  </VStack>
                ) : (
                  <Center p={10}><Text color="gray.500">No hay torneos con inscripciones abiertas.</Text></Center>
                )}
              </TabPanel>
              {/* Pestaña de Torneos en Curso */}
              <TabPanel p={4}>
                {inProgressTournaments.length > 0 ? (
                  <VStack align="stretch" spacing={6}>
                    <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
                      {paginatedInProgress.map(tournament => (
                        <InfoTournamentCard key={tournament.id} tournament={tournament} onClick={() => handleBracketClick(tournament)} />
                      ))}
                    </SimpleGrid>
                     {totalInProgressPages > 1 && (
                      <HStack justify="center" mt={4}>
                        <IconButton icon={<FaArrowLeft />} aria-label="Anterior" onClick={() => setInProgressPage(p => p - 1)} isDisabled={inProgressPage === 0} />
                        <Text fontSize="sm" color="gray.600">{inProgressPage + 1} / {totalInProgressPages}</Text>
                        <IconButton icon={<FaArrowRight />} aria-label="Siguiente" onClick={() => setInProgressPage(p => p + 1)} isDisabled={inProgressPage >= totalInProgressPages - 1} />
                      </HStack>
                    )}
                  </VStack>
                ) : (
                  <Center p={10}><Text color="gray.500">No hay torneos en curso en este momento.</Text></Center>
                )}
              </TabPanel>
            </TabPanels>
          </Tabs>
        </Box>

        {/* ✅ CONTENEDOR INFERIOR AHORA ES EL HISTORIAL */}
        <Box>
          <Heading size="xl" mb={4}>Historial de Torneos</Heading>
          {pastTournaments.length > 0 ? (
            <VStack align="stretch" spacing={6}>
              <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
                {paginatedPast.map(tournament => (
                  <InfoTournamentCard key={tournament.id} tournament={tournament} onClick={() => handleBracketClick(tournament)} />
                ))}
              </SimpleGrid>
              {totalPastPages > 1 && (
                  <HStack justify="center" mt={4}>
                    <IconButton icon={<FaArrowLeft />} aria-label="Anterior" onClick={() => setPastPage(p => p - 1)} isDisabled={pastPage === 0} />
                    <Text fontSize="sm" color="gray.600">{pastPage + 1} / {totalPastPages}</Text>
                    <IconButton icon={<FaArrowRight />} aria-label="Siguiente" onClick={() => setPastPage(p => p + 1)} isDisabled={pastPage >= totalPastPages - 1} />
                  </HStack>
                )}
            </VStack>
          ) : (
            <Center p={10} bg="white" borderRadius="lg" boxShadow="md">
              <Text color="gray.500">Aún no hay torneos en el historial.</Text>
            </Center>
          )}
        </Box>
      </VStack>
    </Container>
  );
}